// This is the component for the JSA V2 control measures section.
// Basix component will be the checkbox component, which will be nested in a components that can be used to collapse a specific section of checkboxes decluttering the UI and making the form easier to use.
import { useEffect, useMemo, useRef } from "react";
import styles from "./jsaV2CmComponents.module.css";
import { SECTION_ATTRIBUTES } from "../../../constants/jsaConstants";

// Helper function to get field error for V2 control measures
const getControlMeasuresV2FieldError = (autosaveErrors, fieldName) => {
  const key = `jsa_control_measures_v2.${fieldName}`;
  return autosaveErrors?.fieldErrors?.[key] || null;
};

// This is the checkbox component for the hazards section.
export const JsaV2CmCheckbox = ({ callbacks, label, attribute }) => {
  // Handling the change of the checkbox
  const handleChange = (e) => {
    callbacks.v2Actions.updateJsaV2Cm(
      callbacks.jsaData,
      attribute,
      e.target.checked,
      callbacks.emitPatch
    );
    // Commit immediately if provided
    try {
      const toSnake = (s) =>
        String(s).replace(/[A-Z]/g, (L) => `_${L.toLowerCase()}`);
      callbacks?.commit?.controlMeasure?.(
        callbacks.jsaData,
        toSnake(attribute),
        e.target.checked
      );
    } catch {}
  };

  return (
    <div className={styles.jsaV2CmCheckbox}>
      <label>{label}</label>
      <input
        type="checkbox"
        checked={callbacks.jsaData.jsaControlMeasuresV2[attribute]}
        onChange={handleChange}
        disabled={callbacks.jsaMode === "view"}
      />
    </div>
  );
};

// The following will be components that house specific groups of checkboxes. These sections will be collapsible (on mobile) but will be set up in different groups for larger screens.

// Confined Space Section
export const JsaV2CmCseSection = ({ callbacks }) => {
  return (
    <div className={styles.jsaV2CmSection}>
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Surveyed"
        attribute="csSurveyed"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Air Monitoring"
        attribute="csAirMon"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Re System"
        attribute="csReSys"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Trained Personnel"
        attribute="csTrainedPerson"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Competent Person"
        attribute="csCompPerson"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Risk Assessed"
        attribute="csRiskAssess"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Rescue"
        attribute="csRescue"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Communication"
        attribute="csComms"
      />
    </div>
  );
};

// Electrical Section
export const JsaV2CmElectricalSection = ({ callbacks }) => {
  return (
    <div className={styles.jsaV2CmSection}>
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Lockout/Tagout"
        attribute="elecLoto"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Grounded"
        attribute="elecGrounded"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Panel Covered"
        attribute="elecPanelCover"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="GFCI/Extension Cords"
        attribute="elecGfciExtCord"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Power Tools Cord Inspected"
        attribute="elecPtCordInsp"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Overhead Lines"
        attribute="elecOverheadLine"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Warning Signs"
        attribute="elecWarningSigns"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Spotter"
        attribute="elecSpotter"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Hand/Hydro Excavate"
        attribute="elecHandHydroExcav"
      />
    </div>
  );
};

// Equipment Section
export const JsaV2CmEquipmentSection = ({ callbacks }) => {
  return (
    <div className={styles.jsaV2CmSection}>
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Suitable"
        attribute="equipSuitable"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Inspect Lifting Gear"
        attribute="equipInspLift"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="All Warning Devices"
        attribute="equipWarningDev"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Seat Belts (mandatory)"
        attribute="equipSeatBelts"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Exclusion Zone"
        attribute="equipExcZone"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Pedestrian Route"
        attribute="equipPedRoute"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Fencing"
        attribute="equipFencing"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Guzzler Safety Training"
        attribute="equipGuzzlerSafety"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Safe Working Load"
        attribute="equipSafeWorkLoad"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Locking Attachments (buckets, etc.)"
        attribute="equipLockAttach"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Flagger Needed"
        attribute="equipFlagger"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Rigger/Signaler"
        attribute="equipRigger"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Speed Signs"
        attribute="equipSpeedSign"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="No Passengers"
        attribute="equipNoPass"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="No Personal Cellphones"
        attribute="equipNoCellphone"
      />
    </div>
  );
};

// Excavation Section
export const JsaV2CmExcavationSection = ({ callbacks }) => {
  return (
    <div className={styles.jsaV2CmSection}>
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Sloping"
        attribute="excavSloping"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Shoring"
        attribute="excavShoring"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Trench Box"
        attribute="excavTrenchBox"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Barricades"
        attribute="excavBarricades"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Competent Person"
        attribute="excavCompPerson"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Locate Buried Utilities"
        attribute="excavBuriedUtil"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Daily Inspection"
        attribute="excavInspection"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Exclusion Zone"
        attribute="excavExcZone"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="No Undermining"
        attribute="excavUndermining"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Spoils Back 2ft Min."
        attribute="excavSpoilsMin"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Vehicle Wheels Chocked"
        attribute="excavVehWheelChock"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Access/Egress Adequate"
        attribute="excavAdequateAccess"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Holes Covered - When Not In Use"
        attribute="excavHoleCover"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Warning Signs"
        attribute="excavWarningSigns"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Materials Stored >2ft from Edge"
        attribute="excavMatEdgeStorage"
      />
    </div>
  );
};

// Fall Protection Section
export const JsaV2CmFallProtectionSection = ({ callbacks }) => {
  return (
    <div className={styles.jsaV2CmSection}>
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Harness/Lanyard"
        attribute="fallHarnessLanyard"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Adequate Anchorage"
        attribute="fallAdequateAnchor"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Guardrail System"
        attribute="fallGuardrail"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Covered Opening"
        attribute="fallCoveredOpenings"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Fixed Barricades"
        attribute="fallFixedBarricades"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Warning System"
        attribute="fallWarningSystem"
      />
    </div>
  );
};

// Fire Protection Section
export const JsaV2CmFireProtectionSection = ({ callbacks }) => {
  return (
    <div className={styles.jsaV2CmSection}>
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Fire Extinguisher"
        attribute="fireExtinguisher"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Fire Watch"
        attribute="fireWatch"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Nonspark Tools"
        attribute="fireNonsparkTools"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Grounding/Bonding"
        attribute="fireGrounding"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Intrinsically Safe Equipment"
        attribute="fireIntrinSafeEquip"
      />
    </div>
  );
};

// Hand Tools Section
export const JsaV2CmHandToolsSection = ({ callbacks }) => {
  return (
    <div className={styles.jsaV2CmSection}>
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Check Suitability"
        attribute="htSuitability"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Voltage Known"
        attribute="htVoltageKnown"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Inspect Cord"
        attribute="htInspectCord"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Functional Guard in Place"
        attribute="htFuncGuard"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Generator Outside"
        attribute="htGeneratorOut"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Tools Repaired"
        attribute="htToolsRep"
      />
    </div>
  );
};

// Heat/Cold Stress Section
export const JsaV2CmHeatColdStressSection = ({ callbacks }) => {
  return (
    <div className={styles.jsaV2CmSection}>
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Work/Rest Regimen"
        attribute="hcWorkRest"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Rest Area"
        attribute="hcRestArea"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Liquids Available"
        attribute="hcLiqAvail"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Monitoring"
        attribute="hcMonitoring"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Training"
        attribute="hcTraining"
      />
    </div>
  );
};

// Inspections Section
export const JsaV2CmInspectionsSection = ({ callbacks }) => {
  return (
    <div className={styles.jsaV2CmSection}>
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Ladders/Aerial Lifts"
        attribute="inspLadAerial"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Lanyards/Harnesses"
        attribute="inspLanHarness"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Scaffolds"
        attribute="inspScaffold"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Heavy Equipment"
        attribute="inspHeavyEquip"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Cranes and Rigging"
        attribute="inspCraneRig"
      />
    </div>
  );
};

// Medical ER Section
export const JsaV2CmMedicalErSection = ({ callbacks }) => {
  return (
    <div className={styles.jsaV2CmSection}>
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="First-aid Kit    "
        attribute="medFirstAid"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Eye Wash"
        attribute="medEyeWash"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="FA/CPR Trained"
        attribute="medCpr"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Route to Hospital"
        attribute="medHopsitalRoute"
      />
    </div>
  );
};

// Permits Section
export const JsaV2CmPermitsSection = ({ callbacks }) => {
  return (
    <div className={styles.jsaV2CmSection}>
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Hot Work"
        attribute="permHotWork"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Confined Space"
        attribute="permConfinedSpace"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Lockout/Tagout"
        attribute="permLoto"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Excavation"
        attribute="permExcavation"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Demolition"
        attribute="permDemo"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Energized Work"
        attribute="permEnergized"
      />
    </div>
  );
};

// PPE Section
export const JsaV2CmPpeSection = ({ callbacks }) => {
  return (
    <div className={styles.jsaV2CmSection}>
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Thermal/Lined"
        attribute="ppeThermal"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Eyes-Safety glasses min"
        attribute="ppeEye"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Dermal/Hand"
        attribute="ppeDermalHand"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Hearing- If Shouting at arm's length required"
        attribute="ppeHearing"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Respirator"
        attribute="ppeRespirator"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Supplied Air"
        attribute="ppeSuppAir"
      />
      <JsaV2CmCheckbox callbacks={callbacks} label="APR" attribute="ppeApr" />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Control APR Cartridges"
        attribute="ppeControlAprCart"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Reflective Vests"
        attribute="ppeRefVest"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Floatation Devices"
        attribute="ppeFloatation"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Hard Hats"
        attribute="ppeHardHat"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Work Boots"
        attribute="ppeWorkboot"
      />
    </div>
  );
};

// Protection of Public Section
export const JsaV2CmPopSection = ({ callbacks }) => {
  return (
    <div className={styles.jsaV2CmSection}>
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Fencing"
        attribute="popFencing"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Covered Walkway"
        attribute="popCoveredWalkway"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Barriers"
        attribute="popBarriers"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Pedestrian Walkway"
        attribute="popPedWalkway"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Warning Signs"
        attribute="popWarningSigns"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Security"
        attribute="popSecurity"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Flagger"
        attribute="popFlagger"
      />
    </div>
  );
};

// Training Section
export const JsaV2CmTrainingSection = ({ callbacks }) => {
  return (
    <div className={styles.jsaV2CmSection}>
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="HAZWOPER"
        attribute="trainHazwoper"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Construction"
        attribute="trainConstruction"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Competent Person"
        attribute="trainCompPerson"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Task Specific"
        attribute="trainTaskSpecific"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Hazcom"
        attribute="trainHazcom"
      />
    </div>
  );
};

// Vehicle Traffic Section
export const JsaV2CmVehicleTrafficSection = ({ callbacks }) => {
  return (
    <div className={styles.jsaV2CmSection}>
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Traffic Control"
        attribute="vtTrafControl"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Barricades"
        attribute="vtBarricades"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Flags"
        attribute="vtFlags"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Signs"
        attribute="vtSigns"
      />
    </div>
  );
};

// Welding & Cutting Section
export const JsaV2CmWeldingCuttingSection = ({ callbacks }) => {
  return (
    <div className={styles.jsaV2CmSection}>
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Cylinder Capped/Secured"
        attribute="wcCylinderSec"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Cylinders Separated"
        attribute="wcCylinderSep"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Flash-back Arrestors"
        attribute="wcFbArrestor"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="No Cylinder in CSE"
        attribute="wcNoCylCse"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Flame Retardant Cloth"
        attribute="wcFlamRetCloth"
      />
      <JsaV2CmCheckbox
        callbacks={callbacks}
        label="Appropriate Goggles"
        attribute="wcAppGoggles"
      />
    </div>
  );
};

// Map of section names to their checkbox attributes

// Component to hold each section that allows for collapsable buttons for user
export const JsaV2CmSectionContainer = ({
  callbacks,
  label,
  section,
  children,
}) => {
  // Get attributes for this section from the predefined map
  const sectionAttributes = useMemo(
    () => SECTION_ATTRIBUTES[section] || [],
    [section]
  );

  // Check if any of this section's attributes are checked
  const hasCheckedItems = useMemo(() => {
    return sectionAttributes.some(
      (attr) => callbacks.jsaData.jsaControlMeasuresV2[attr] === true
    );
  }, [sectionAttributes, callbacks.jsaData.jsaControlMeasuresV2]);

  // Refs to control one-time auto-expand and allow user overrides
  const didAutoExpandRef = useRef(false);
  const userToggledRef = useRef(false);

  // Auto-expand once on load if any items are checked; allow user to close later
  useEffect(() => {
    if (didAutoExpandRef.current || userToggledRef.current) return;
    const isCurrentlyExpanded = callbacks.jsaV2.controlMeasures[section];
    if (hasCheckedItems && !isCurrentlyExpanded) {
      callbacks.v2Actions.showJsaV2CmSection(section, true);
      didAutoExpandRef.current = true;
    }
    // Intentionally omit controlMeasures from deps so user toggles don't re-open
  }, [
    hasCheckedItems,
    section,
    callbacks.v2Actions,
    callbacks.jsaV2.controlMeasures,
  ]);

  // Handling the click of the button to show/hide the section
  const handleClick = () => {
    userToggledRef.current = true;
    callbacks.v2Actions.showJsaV2CmSection(
      section,
      !callbacks.jsaV2.controlMeasures[section]
    );
  };

  const isExpanded = callbacks.jsaV2.controlMeasures[section];

  return (
    <div className={styles.jsaV2CmSectionContainer}>
      <div
        className={`${styles.jsaV2CmSectionTitle} ${
          hasCheckedItems ? styles.jsaV2CmSectionTitleWithItems : ""
        }`}
        onClick={handleClick}
      >
        <p>{label}</p>
        <button
          className={styles.jsaV2CmToggleButton}
          onClick={handleClick}
          type="button"
          aria-label={`Toggle ${label} section`}
        >
          {isExpanded ? "▼" : "▲"}
        </button>
      </div>
      <div className={styles.jsaV2CmSectionContent}>
        {isExpanded && children}
      </div>
    </div>
  );
};

// Control Measures Info Tab Section
export const JsaV2CmInfoTab = ({ callbacks }) => {
  return (
    <div className={styles.jsaV2CmContainer}>
      <JsaV2CmSectionContainer
        callbacks={callbacks}
        label="Confined Space Entry"
        section="showCseSection"
        children={<JsaV2CmCseSection callbacks={callbacks} />}
      />
      <JsaV2CmSectionContainer
        callbacks={callbacks}
        label="Electrical"
        section="showElectricalSection"
        children={<JsaV2CmElectricalSection callbacks={callbacks} />}
      />
      <JsaV2CmSectionContainer
        callbacks={callbacks}
        label="Equipment"
        section="showEquipmentSection"
        children={<JsaV2CmEquipmentSection callbacks={callbacks} />}
      />
      <JsaV2CmSectionContainer
        callbacks={callbacks}
        label="Excavation"
        section="showExcavationSection"
        children={<JsaV2CmExcavationSection callbacks={callbacks} />}
      />
      <JsaV2CmSectionContainer
        callbacks={callbacks}
        label="Fall Protection"
        section="showFallProtectionSection"
        children={<JsaV2CmFallProtectionSection callbacks={callbacks} />}
      />
      <JsaV2CmSectionContainer
        callbacks={callbacks}
        label="Fire Protection"
        section="showFireProtectionSection"
        children={<JsaV2CmFireProtectionSection callbacks={callbacks} />}
      />
      <JsaV2CmSectionContainer
        callbacks={callbacks}
        label="Hand Tools"
        section="showHandToolsSection"
        children={<JsaV2CmHandToolsSection callbacks={callbacks} />}
      />
      <JsaV2CmSectionContainer
        callbacks={callbacks}
        label="Heat/Cold Stress"
        section="showHeatColdStressSection"
        children={<JsaV2CmHeatColdStressSection callbacks={callbacks} />}
      />
      <JsaV2CmSectionContainer
        callbacks={callbacks}
        label="Inspections"
        section="showInspectionsSection"
        children={<JsaV2CmInspectionsSection callbacks={callbacks} />}
      />
      <JsaV2CmSectionContainer
        callbacks={callbacks}
        label="Medical/ER"
        section="showMedicalSection"
        children={<JsaV2CmMedicalErSection callbacks={callbacks} />}
      />
      <JsaV2CmSectionContainer
        callbacks={callbacks}
        label="Permits"
        section="showPermitsSection"
        children={<JsaV2CmPermitsSection callbacks={callbacks} />}
      />
      <JsaV2CmSectionContainer
        callbacks={callbacks}
        label="PPE"
        section="showPpeSection"
        children={<JsaV2CmPpeSection callbacks={callbacks} />}
      />
      <JsaV2CmSectionContainer
        callbacks={callbacks}
        label="Protection of Public"
        section="showPopSection"
        children={<JsaV2CmPopSection callbacks={callbacks} />}
      />
      <JsaV2CmSectionContainer
        callbacks={callbacks}
        label="Training"
        section="showTrainingSection"
        children={<JsaV2CmTrainingSection callbacks={callbacks} />}
      />
      <JsaV2CmSectionContainer
        callbacks={callbacks}
        label="Vehicle Traffic"
        section="showVehicleTrafficSection"
        children={<JsaV2CmVehicleTrafficSection callbacks={callbacks} />}
      />
      <JsaV2CmSectionContainer
        callbacks={callbacks}
        label="Welding & Cutting"
        section="showWeldingCuttingSection"
        children={<JsaV2CmWeldingCuttingSection callbacks={callbacks} />}
      />
    </div>
  );
};
